import Context from '@/context';
import type { ICircuitModel, ILogicCircuitModel } from '@/interfaces/models';
import { LogicCircuitModel } from '@/models/LogicCircuitModel';
import { reaction } from 'mobx';

export class LogicCircuitModelFactory {
  constructor(
    private readonly deferVisualPublication = false,
    private readonly compileOnly = false,
  ) {}
  create(
    circuitModel: ICircuitModel,
    simulationConfig: {
      maxSyncIterations: number;
      asyncDelay: number;
      maximumSpeed?: boolean;
    },
    context: Context,
  ): ILogicCircuitModel {
    // Model consumers remain synchronous by default. The browser opt-in tracks
    // the published app context, which is assigned after restored graphs exist.
    const isObserved = () => {
      const editor = context.app?.context?.editor;
      return !editor || editor.currentChip?.logicCircuit?.circuitModel === circuitModel;
    };
    let observed = true;
    return new LogicCircuitModel(
      circuitModel,
      simulationConfig,
      context,
      this.deferVisualPublication
        ? {
            request: (callback) => requestAnimationFrame(callback),
            cancel: (handle) => cancelAnimationFrame(handle),
            isVisible: () => document.visibilityState !== 'hidden',
            // Resolve observable editor ownership on changes, then read a plain
            // boolean in the frame callback shared by every active circuit.
            isObserved: () => observed,
            onObserved: (callback) =>
              reaction(
                () => {
                  observed = isObserved();
                  return observed;
                },
                (next) => {
                  if (next) callback();
                },
              ),
            onHidden: (callback) => {
              const changed = () => {
                if (document.visibilityState === 'hidden') callback();
              };
              document.addEventListener('visibilitychange', changed);
              return () => document.removeEventListener('visibilitychange', changed);
            },
          }
        : undefined,
      this.compileOnly,
    );
  }
}
